import React from "react";
import { Box, Link, Typography } from "@mui/material";

const SECTIONS = [
  {
    title: "School details",
    rows: (p) => [
      ["Type of school", p.details?.type],
      ["Website", p.details?.website, p.details?.website],
      ["Address", p.details?.address],
      ["Phone", p.details?.phone],
      ["Email", p.details?.email],
      ["Consortium", p.details?.consortium],
      ["Region", p.details?.region],
    ],
  },
  {
    title: "Admissions",
    rows: (p) => [
      ["Admission year", p.admissions?.entry_label],
      ["Gender in Year 7", p.admissions?.gender],
    ],
  },
  {
    title: "Calendar",
    rows: (p) => [
      ["CAF form", p.calendar?.caf_deadline],
      ["Allocation day", p.calendar?.allocation_date],
      ["Registration", p.calendar?.registration],
    ],
  },
  {
    title: "Academic selection",
    rows: (p) => [
      ["Number of places", p.selection?.places],
      ["Standardised", p.selection?.standardised],
      ["Age standardised", p.selection?.age_standardised],
      ["Number of rounds", p.selection?.rounds],
      ["Qualifying score", p.selection?.qualifying_score],
      ["Admission policy", p.selection?.admission_policy_url ? "View" : null, p.selection?.admission_policy_url],
    ],
  },
];

function external(url) {
  if (!url) return null;
  return /^https?:\/\//.test(url) ? url : `https://${url}`;
}

function Rows({ rows }) {
  const visible = rows.filter((row) => row[1] != null && row[1] !== "");
  if (!visible.length) return null;
  return (
    <Box>
      {visible.map(([label, value, href]) => (
        <Box key={label} sx={{ display: "grid", gridTemplateColumns: "160px minmax(0, 1fr)", gap: 1, py: 0.4 }}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          {href ? (
            <Link href={external(href)} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
              {value}
            </Link>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>{value}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function SchoolExamProfile({ profile }) {
  if (!profile) return null;
  const papers = profile.exam_papers || [];
  const samples = profile.sample_papers || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
      {profile.format_line && (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{profile.format_line}</Typography>
      )}
      {SECTIONS.map((section) => {
        const rows = section.rows(profile);
        if (!rows.some((row) => row[1] != null && row[1] !== "")) return null;
        return (
          <Box key={section.title}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{section.title}</Typography>
            <Rows rows={rows} />
          </Box>
        );
      })}
      {(profile.exam_board || profile.exam_style || profile.subjects) && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Exam</Typography>
          <Rows rows={[
            ["Exam board", profile.exam_board],
            ["Exam style", profile.exam_style],
            ["Subjects", profile.subjects],
          ]} />
        </Box>
      )}
      {samples.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Sample papers</Typography>
          {samples.map((paper) => (
            <Box key={`${paper.name}-${paper.url}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.4 }}>
              <Typography variant="body2">
                <strong>{paper.name}</strong>
                {paper.subjects ? ` \u00b7 ${paper.subjects}` : ""}
              </Typography>
              {paper.url && (
                <Link href={paper.url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ fontWeight: 700 }}>View</Link>
              )}
            </Box>
          ))}
        </Box>
      )}
      {papers.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Exam papers</Typography>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& td, & th": { textAlign: "left", py: 0.4, pr: 1, fontSize: 14, verticalAlign: "top" }, "& th": { color: "text.secondary" } }}>
            <thead>
              <tr><th>Name</th><th>Round</th><th>Minutes</th><th>Subjects</th></tr>
            </thead>
            <tbody>
              {papers.map((paper) => (
                <tr key={`${paper.name}-${paper.round}`}>
                  <td>{paper.name}</td>
                  <td>{paper.round ?? ""}</td>
                  <td>{paper.minutes || ""}</td>
                  <td>{paper.subjects || ""}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        These figures follow the school’s published admissions and can change.
      </Typography>
    </Box>
  );
}
